/**
 * View registry — every workspace-eligible page declares itself here.
 *
 * Sidebar nav clicks resolve via `resolvePathToView`. URL paths and view IDs
 * are interchangeable; the workspace store renders by view ID, the URL
 * shows the path corresponding to the focused panel's active view.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import {
  LayoutDashboard, FileText, Activity, LineChart, Sparkles,
  FileClock, Heart, CandlestickChart, Gauge, Radar, Target, Layers, Filter, Settings as SettingsIcon,
  CircleDollarSign, BookOpen, BookMarked, Zap, Columns3, ShieldCheck, Crosshair,
  Bird, ScrollText,
  type LucideIcon,
} from 'lucide-react'
import { isZeroDteCondorEnabled } from '@/lib/desk/zeroDteCondor/flags'

export interface ViewDefinition {
  id: string
  title: string
  path: string                    // canonical route
  icon: LucideIcon
  component: LazyExoticComponent<ComponentType>
  /**
   * True when the view is safe to open more than once — meaning it keeps its
   * working state per instance (via `useViewState`) rather than in component
   * state or a global localStorage key.
   *
   * ENFORCED: the "+" menu, Ctrl+click-to-split and the split controls all
   * refuse to duplicate a view without it, and focus the existing instance
   * instead. A view that isn't instance-safe would otherwise blank itself the
   * moment you switched tabs, which is exactly the bug this flag now prevents.
   */
  multi?: boolean
  /**
   * Param keys this view carries in its URL, e.g. `['symbol']` →
   * `/options-chain?symbol=TSLA`. Makes the view deep-linkable and lets
   * pop-out carry what the tab was looking at. Views that declare none keep a
   * bare path.
   */
  urlParams?: string[]
  /** If true, omit from the sidebar nav (still routable + can open in workspace). */
  hideFromNav?: boolean
  /** If true, render edge-to-edge with no padding/scroll (the view manages its own layout). */
  fullBleed?: boolean
  /** Legacy/alternate paths that should still resolve to this view (e.g. a
   *  renamed route's old URL). Checked after the exact-path match in
   *  `resolvePathToView`, both as-given and slash-stripped. */
  aliases?: string[]
}

export const VIEW_REGISTRY: Record<string, ViewDefinition> = {
  dashboard: {
    id: 'dashboard',
    title: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
    component: lazy(() => import('@/pages/Dashboard')),
    // Read-only cards, no component state at all — two copies can't disagree.
    multi: true,
  },
  trading: {
    id: 'trading',
    title: 'Trading',
    path: '/trading',
    icon: CandlestickChart,
    component: lazy(() => import('@/pages/Trading')),
  },
  tracker: {
    id: 'tracker',
    title: 'Tracker',
    path: '/tracker',
    icon: CircleDollarSign,
    component: lazy(() => import('@/pages/Tracker')),
  },
  journal: {
    id: 'journal',
    title: 'Journal',
    path: '/journal',
    icon: ScrollText,
    component: lazy(() => import('@/pages/Journal')),
  },
  book: {
    id: 'book',
    title: 'Book',
    path: '/book',
    icon: BookMarked,
    component: lazy(() => import('@/pages/Book')),
  },
  // Desk family — own lane, not nested under Book/Screener. Flag: compass.0dte_condor
  zeroDteCondor: {
    id: 'zeroDteCondor',
    title: '0DTE Condor',
    path: '/desk/0dte-condor',
    icon: Bird,
    component: lazy(() => import('@/pages/ZeroDteCondor')),
    fullBleed: true,
    hideFromNav: !isZeroDteCondorEnabled(),
  },
  ledger: {
    id: 'ledger',
    title: 'Ledger',
    path: '/ledger',
    icon: BookOpen,
    component: lazy(() => import('@/pages/Ledger')),
  },
  optionsChain: {
    id: 'optionsChain',
    title: 'Options Chain',
    path: '/options-chain',
    icon: Columns3,
    component: lazy(() => import('@/pages/OptionsChain')),
    // Instance-safe as of the per-instance viewState migration: symbol,
    // expiry, column config and roll state all live on the view instance.
    multi: true,
    urlParams: ['symbol'],
  },
  execEvents: {
    id: 'execEvents',
    title: 'Execution Events',
    path: '/execution-events',
    icon: Zap,
    component: lazy(() => import('@/pages/ExecutionEvents')),
    // The only place fills and their underlying price captures are visible.
    // Was hidden 2026-07-31 as "diagnostic, not a daily view" — reversed
    // 2026-08-04: Ryan uses it to verify capture is working, and read-only
    // verification surfaces are exactly what the agent-first UI keeps.
  },
  risk: {
    id: 'risk',
    title: 'Risk',
    path: '/risk',
    icon: ShieldCheck,
    component: lazy(() => import('@/pages/RiskAssessment')),
    // retired to a Dashboard card 2026-08-04 (Ryan: never opened); route stays live, nav entry hidden. Code deletion lands in the Tier D pass.
    hideFromNav: true,
  },
  baseSelector: {
    id: 'baseSelector',
    title: 'Base Selector',
    path: '/base-selector',
    icon: Crosshair,
    component: lazy(() => import('@/pages/BaseSelector')),
  },
  strategies: {
    id: 'strategies',
    title: 'Strategies',
    path: '/strategies',
    icon: Layers,
    component: lazy(() => import('@/pages/Strategies')),
  },
  screener: {
    id: 'screener',
    title: 'Screener',
    path: '/screener',
    icon: Filter,
    component: lazy(() => import('@/pages/Screener')),
  },
  simulator: {
    id: 'simulator',
    title: 'Charts',
    path: '/charts',
    icon: CandlestickChart,
    // Gate keeps the live Charts cockpit and, when ?replay=<session> is set,
    // mounts that same ReplayChart on the server cursor (no bars after it).
    component: lazy(() => import('@/pages/ChartsReplayGate')),
    multi: true,
    urlParams: ['symbol', 'replay'],
    fullBleed: true,
    aliases: ['/simulator'],
  },
  plans: {
    id: 'plans',
    title: 'Plans',
    path: '/plans',
    icon: FileText,
    component: lazy(() => import('@/pages/Plans')),
  },
  live: {
    id: 'live',
    title: 'Live',
    path: '/live',
    icon: Activity,
    component: lazy(() => import('@/pages/Live')),
  },
  backtests: {
    id: 'backtests',
    title: 'Backtests',
    path: '/backtests',
    icon: LineChart,
    component: lazy(() => import('@/pages/Backtests')),
    multi: true,
  },
  autotune: {
    id: 'autotune',
    title: 'Autotune',
    path: '/autotune',
    icon: Sparkles,
    component: lazy(() => import('@/pages/Autotune')),
    // retired 2026-08-04 — becomes a VIKI job (agent-first pivot); route stays live, nav entry hidden. Code deletion lands in the Tier D pass.
    hideFromNav: true,
    multi: true,
  },
  strategyTester: {
    id: 'strategyTester',
    title: 'Strategy Tester',
    path: '/strategy-tester',
    icon: Gauge,
    component: lazy(() => import('@/pages/StrategyTester')),
    // retired 2026-08-04 — becomes a VIKI job (agent-first pivot); route stays live, nav entry hidden. Code deletion lands in the Tier D pass.
    hideFromNav: true,
    multi: true,
  },
  indicatorProfiles: {
    id: 'indicatorProfiles',
    title: 'Indicator Profiles',
    path: '/indicator-profiles',
    icon: Radar,
    component: lazy(() => import('@/pages/IndicatorProfiles')),
  },
  executionProfiles: {
    id: 'executionProfiles',
    title: 'Execution Profiles',
    path: '/execution-profiles',
    icon: Target,
    component: lazy(() => import('@/pages/ExecutionProfiles')),
  },
  briefs: {
    id: 'briefs',
    title: 'Briefs',
    path: '/briefs',
    icon: FileClock,
    component: lazy(() => import('@/pages/Briefs')),
  },
  health: {
    id: 'health',
    title: 'Health',
    path: '/health',
    icon: Heart,
    component: lazy(() => import('@/pages/Health')),
  },
  settings: {
    id: 'settings',
    title: 'User Settings',
    path: '/settings',
    icon: SettingsIcon,
    component: lazy(() => import('@/pages/Settings')),
    hideFromNav: true,
  },
}

export const NAV_ITEMS = Object.values(VIEW_REGISTRY).filter((v) => !v.hideFromNav)

/**
 * Resolve a location to a view. `search` is the raw query string; only keys the
 * view declares in `urlParams` are read, so an unrelated query param can never
 * change which view resolves or churn the URL↔store sync.
 */
export function resolvePathToView(
  path: string, search?: string,
): { viewId: string; params?: Record<string, string> } | null {
  // Resolve against ALL views (including hidden-from-nav ones like /settings),
  // not just NAV_ITEMS.
  const all = Object.values(VIEW_REGISTRY)
  const clean = path.replace(/^\//, '')
  const view = all.find((v) => v.path === path)
    ?? all.find((v) => v.path.replace(/^\//, '') === clean)
    // Legacy alias paths (e.g. a renamed route's old URL) — checked after the
    // exact-path match so a real view never loses to someone else's alias.
    ?? all.find((v) => v.aliases?.includes(path) || v.aliases?.includes(clean))
  if (!view) return null
  return { viewId: view.id, params: parseViewParams(view, search) }
}

/** The declared params present in a query string, or undefined if none are. */
export function parseViewParams(
  view: ViewDefinition, search?: string,
): Record<string, string> | undefined {
  if (!view.urlParams?.length || !search) return undefined
  const q = new URLSearchParams(search)
  const out: Record<string, string> = {}
  for (const k of view.urlParams) {
    const v = q.get(k)
    if (v) out[k] = v
  }
  return Object.keys(out).length ? out : undefined
}

/**
 * Canonical URL for a view instance — path plus its declared params.
 *
 * Key order follows `urlParams`, not the params object, so the same instance
 * always produces the same string. The URL↔store sync compares these by
 * equality; an unstable encoding here would ping-pong.
 */
export function getViewPath(viewId: string, params?: Record<string, string>): string {
  const view = VIEW_REGISTRY[viewId]
  if (!view) return '/'
  if (!view.urlParams?.length || !params) return view.path
  const q = new URLSearchParams()
  for (const k of view.urlParams) {
    const v = params[k]
    if (v) q.set(k, v)
  }
  const s = q.toString()
  return s ? `${view.path}?${s}` : view.path
}
